/**
 * Centralized error logging utility for the web console.
 *
 * This module provides consistent error logging across the application,
 * making debugging easier by ensuring all errors are logged with context.
 *
 * Sentry Integration:
 *   Set VITE_SENTRY_DSN environment variable to enable Sentry error tracking.
 *   Requires @sentry/react package: npm install @sentry/react
 *
 * Usage:
 *   import { logError } from '@/core/utils/errorLogger'
 *
 *   try {
 *     await someOperation()
 *   } catch (err) {
 *     logError('MyComponent.someOperation', err)
 *   }
 */

// Sentry module interface for dynamic import
interface SentryModule {
  init: (options: {
    dsn: string;
    environment?: string;
    enabled?: boolean;
    tracesSampleRate?: number;
  }) => void;
  captureException: (
    error: unknown,
    options?: { tags?: Record<string, string> }
  ) => void;
}

let Sentry: SentryModule | null = null;
let sentryInitialized = false;

/**
 * Initialize Sentry if DSN is configured.
 * This is called automatically on first error, but can be called earlier
 * (e.g., in main.tsx) to capture errors from application startup.
 */
export async function initSentry(): Promise<void> {
  if (sentryInitialized) return;
  sentryInitialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  try {
    // Dynamic import - will fail gracefully if @sentry/react is not installed
    // Use variable to prevent TypeScript from resolving at compile time
    const sentryModuleName = "@sentry/react";
    Sentry = (await import(
      /* @vite-ignore */ sentryModuleName
    )) as SentryModule;
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      // Only send errors in production by default
      enabled: import.meta.env.PROD,
      // Adjust sample rate as needed
      tracesSampleRate: 0.1,
    });
  } catch {
    // @sentry/react not installed - that's fine, just use console logging
    console.debug("[errorLogger] Sentry SDK not available");
  }
}

/**
 * Strips potentially sensitive fields (e.g. auth tokens) from errors before
 * they are sent to an external reporting service.
 */
function sanitizeError(error: unknown): unknown {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  return error;
}

/**
 * Logs an error with context information.
 *
 * @param context - A descriptive string identifying where the error occurred
 *                  (e.g., 'AuthProvider.refreshUser', 'PipelineList.handleDeploy')
 * @param error - The error that was caught (can be any type)
 */
export function logError(context: string, error: unknown): void {
  console.error(`[${context}]`, error);

  const sanitized = sanitizeError(error);

  // Send to Sentry if configured
  if (Sentry) {
    Sentry.captureException(sanitized, {
      tags: { context },
    });
  } else if (!sentryInitialized) {
    // Lazily initialize Sentry on first error
    initSentry().then(() => {
      if (Sentry) {
        Sentry.captureException(sanitized, {
          tags: { context },
        });
      }
    });
  }
}
